<template>
  <div class="tabstory-page">

    <div class="layout">

      <!-- ── 参数面板 ── -->
      <div class="params-panel">

        <!-- 原始问题输入 -->
        <div class="question-box">
          <div class="param-label">💬 粘贴 QUORA 原始问题</div>
          <textarea
            v-model="state.question"
            class="question-textarea"
            :disabled="state.selfQA"
            :placeholder="questionPlaceholder"
          />
          <div class="question-hint">AI 会直接针对这个问题生成回答，让内容更自然贴合</div>
          <div class="chip-row">
            <div
              class="self-qa-chip"
              :class="{ active: state.selfQA }"
              @click="toggleSelfQA"
            >✏️ 自问自答</div>
            <div
              class="self-qa-chip ws-chip"
              :class="{ active: sources.items.length > 0, loading: sources.searching, disabled: webSearchDisabled }"
              :title="webSearchDisabled ? '请先粘贴问题或关闭「自问自答」' : '使用 Gemini 联网搜索这个问题的参考资料'"
              @click="onWebSearch"
            >
              <span v-if="sources.searching" class="mini-spinner"></span>
              <template v-else>🔍</template>
              {{ sources.searching ? '搜索中…' : (sources.items.length ? `已检索 ${sources.items.length} 条` : '联网搜索') }}
            </div>
            <div
              v-if="sources.items.length"
              class="self-qa-chip ws-chip ws-action"
              @click="sources.expanded = !sources.expanded"
            >{{ sources.expanded ? '收起素材' : '查看素材' }}</div>
            <div
              v-if="sources.items.length || sources.text"
              class="self-qa-chip ws-chip ws-clear"
              @click="clearSources"
            >× 清空</div>
          </div>

          <!-- 检索素材面板 -->
          <div v-if="sources.expanded && sources.items.length" class="sources-panel">
            <div
              v-for="(it, i) in sources.items"
              :key="i"
              class="source-item"
            >
              <div class="source-title">
                <span class="source-no">{{ i + 1 }}</span>
                <span class="source-name">{{ it.title || '(no title)' }}</span>
                <a
                  v-if="it.url"
                  class="source-link"
                  :href="it.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >↗</a>
              </div>
              <div v-if="it.snippet" class="source-snippet">{{ it.snippet }}</div>
            </div>
            <div class="sources-tip">这些素材会作为「参考资料」拼到生成 prompt 末尾</div>
          </div>
        </div>

        <!-- 2. 目标受众 -->
        <div class="param-section" v-if="options.audiences.length">
          <div class="param-label"><span class="step">2</span> 目标受众</div>
          <div class="pill-group">
            <div
              v-for="it in options.audiences"
              :key="it.value"
              class="pill"
              :class="{ selected: state.audience === it.value }"
              @click="state.audience = it.value"
            >
              <span class="check-icon">✓</span> {{ it.label }}
            </div>
          </div>
        </div>

        <!-- 3. 核心差异化角度 -->
        <div class="param-section" v-if="options.angles.length">
          <div class="param-label"><span class="step">3</span> 核心差异化角度</div>
          <div class="angle-grid">
            <div
              v-for="it in options.angles"
              :key="it.value"
              class="angle-card"
              :class="{ selected: state.angle === it.value }"
              @click="state.angle = it.value"
            >{{ it.label }}</div>
          </div>
        </div>

        <!-- 4. 关键词（可多选） -->
        <div class="param-section" v-if="options.keywords.length">
          <div class="param-label">
            <span class="step">4</span> 关键词
            <span class="note">（可多选）</span>
          </div>
          <div class="pill-group">
            <div
              v-for="kw in options.keywords"
              :key="kw.label"
              class="pill"
              :class="{ selected: state.keywords.includes(kw.label) }"
              @click="toggleKeyword(kw.label)"
            >
              <span class="check-icon">✓</span> {{ kw.label }}
            </div>
          </div>
        </div>

        <!-- 5. 语气风格 -->
        <div class="param-section" v-if="options.tones.length">
          <div class="param-label"><span class="step">5</span> 语气风格</div>
          <div class="pill-group">
            <div
              v-for="it in options.tones"
              :key="it.value"
              class="pill"
              :class="{ selected: state.tone === it.value }"
              @click="state.tone = it.value"
            >
              <span class="check-icon">✓</span> {{ it.label }}
            </div>
          </div>
        </div>

        <!-- 6. 输出语言 -->
        <div class="param-section" v-if="options.languages.length">
          <div class="param-label">
            <span class="step">6</span> 输出语言
            <span class="note">（非中文语言均附中文参考译文）</span>
          </div>
          <div class="lang-group">
            <div
              v-for="it in options.languages"
              :key="it.value"
              class="lang-pill"
              :class="{ selected: state.language === it.value }"
              @click="state.language = it.value"
            >{{ it.label }}</div>
          </div>
        </div>

        <button
          class="generate-btn"
          :class="{ loading: streaming }"
          :disabled="!canGenerate || streaming"
          @click="onGenerate"
        >
          <span v-if="streaming" class="btn-spinner"></span>
          <span class="btn-text">{{ streaming ? '生成中…' : '✦ 生成帖子' }}</span>
        </button>

      </div>

      <!-- ── 输出面板 ── -->
      <div class="output-panel">
        <div class="output-card">
          <div class="output-header">
            <span class="output-title">生成结果</span>
            <div class="output-actions">
              <div class="status-dot" :class="statusDotClass"></div>
              <button
                v-if="canSave"
                class="copy-btn save-btn"
                :class="{ copied: saved }"
                :disabled="saving"
                @click="onSave"
              >{{ saved ? '✓ 已归档' : (saving ? '保存中…' : '💾 保存到文章管理') }}</button>
              <button
                v-if="canCopy"
                class="copy-btn"
                :class="{ copied: copied }"
                @click="onCopy"
              >{{ copied ? '✓ 已复制' : '复制正文' }}</button>
            </div>
          </div>
          <div class="output-body" ref="outputBodyRef">
            <!-- 占位 -->
            <div class="output-placeholder" v-if="phase === 'idle'">
              <div class="placeholder-icon">✦</div>
              <p>选择左侧参数后，点击<br><strong>生成帖子</strong>，内容将实时流式输出。</p>
            </div>

            <!-- 流式原文 -->
            <pre v-if="phase === 'streaming'" class="stream-raw"
              >{{ streamText }}<span class="cursor"></span></pre>

            <!-- 结构化双语 -->
            <template v-if="phase === 'done'">
              <div class="bilingual-section target-section">
                <div class="bi-header">
                  <span>{{ targetLangLabel }} 帖子正文</span>
                </div>
                <div class="bi-body">{{ structured.target }}</div>
              </div>
              <div v-if="structured.chineseRef" class="bilingual-section chinese-section">
                <div class="bi-header"><span>中文参考译文</span></div>
                <div class="bi-body">{{ structured.chineseRef }}</div>
              </div>
            </template>

            <!-- 错误 -->
            <div v-if="errorMsg" class="error-msg show">{{ errorMsg }}</div>
          </div>

          <div v-if="phase === 'done' && structured.keywordsUsed.length" class="keywords-section show">
            <div class="keywords-label">已覆盖的关键词</div>
            <div class="keyword-chips">
              <div
                v-for="kw in structured.keywordsUsed"
                :key="kw"
                class="keyword-chip"
              >{{ kw }}</div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onActivated, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { tabstoryApi } from '../api'
import { resolveApiBaseURL } from '../utils/appBase'

const DEFAULT_QUESTION_PLACEHOLDER =
  "把 Quora 上的问题粘贴到这里\n\n例：What's the best AI tool to convert a novel into manga without drawing skills?"

const SELF_QA_PLACEHOLDER = "自问自答模式 — AI 会自行拟定问题并回答"

function parseOutput(fullText, language) {
  const chineseMark = /---\s*CHINESE\s+REFERENCE\s*---/i
  const kwMark = /---\s*KEYWORDS\s*---/i
  const chineseMatch = fullText.match(chineseMark)
  const kwIdx = fullText.search(kwMark)

  let kwText = ''
  if (kwIdx > -1) kwText = fullText.slice(kwIdx)

  if (!chineseMatch || language === 'zh') {
    const postText = kwIdx > -1 ? fullText.slice(0, kwIdx).trim() : fullText.trim()
    return { target: postText, chineseRef: '', kwText }
  }

  const splitIdx = fullText.search(chineseMark)
  const target = fullText.slice(0, splitIdx).trim()
  const afterChinese = fullText.slice(splitIdx + chineseMatch[0].length)
  const kwIdx2 = afterChinese.search(kwMark)
  const chineseRef = kwIdx2 > -1 ? afterChinese.slice(0, kwIdx2).trim() : afterChinese.trim()
  if (kwIdx2 > -1) kwText = afterChinese.slice(kwIdx2)
  return { target, chineseRef, kwText }
}

function extractKeywordsUsed(kwText) {
  if (!kwText) return []
  const m = kwText.match(/keywords\s+used\s*:\s*(.+)/i)
  if (!m) return []
  return m[1]
    .split(',')
    .map(s => s.trim().replace(/^["']|["']$/g, ''))
    .filter(Boolean)
}

export default {
  name: 'Tabstory',
  setup() {
    const options = reactive({
      audiences: [],
      angles: [],
      keywords: [],
      tones: [],
      languages: [],
    })
    const loadingOptions = ref(false)

    const state = reactive({
      selfQA: false,
      question: '',
      audience: '',
      angle: '',
      keywords: [],
      tone: '',
      language: 'en',
    })

    const phase = ref('idle')      // idle | streaming | done
    const streamText = ref('')
    const fullText = ref('')
    const errorMsg = ref('')
    const structured = reactive({
      target: '',
      chineseRef: '',
      keywordsUsed: [],
    })

    // 联网搜索（Gemini 3 Pro + google_search）回来的素材
    const sources = reactive({
      text: '',         // 拼好的多行 markdown，会被塞回 generate / save 的 sources_text
      items: [],        // [{title, url, snippet, heat}]
      searching: false,
      expanded: false,
    })

    const webSearchDisabled = computed(() =>
      sources.searching || state.selfQA || !(state.question || '').trim(),
    )
    const saved = ref(false)
    const saving = ref(false)
    const copied = ref(false)
    const streaming = computed(() => phase.value === 'streaming')

    const outputBodyRef = ref(null)

    let abortCtrl = null

    const questionPlaceholder = computed(() =>
      state.selfQA ? SELF_QA_PLACEHOLDER : DEFAULT_QUESTION_PLACEHOLDER,
    )

    const canGenerate = computed(() => {
      if (!state.audience || !state.angle || !state.tone) return false
      if (!state.selfQA && !(state.question || '').trim()) return false
      return true
    })

    const canCopy   = computed(() => phase.value === 'done' && !!structured.target)
    const canSave   = computed(() => phase.value === 'done' && !!fullText.value && !saved.value)

    const statusDotClass = computed(() => ({
      streaming: phase.value === 'streaming',
      done: phase.value === 'done' && !errorMsg.value,
      error: !!errorMsg.value,
    }))

    const targetLangLabel = computed(() => {
      const lang = options.languages.find(l => l.value === state.language)
      return (lang && lang.label) || state.language
    })

    /* ── 工具 ── */

    const toggleSelfQA = () => {
      state.selfQA = !state.selfQA
      if (state.selfQA) {
        state.question = ''
        // 自问自答模式下没有具体问题可搜，清掉已有素材
        clearSources()
      }
    }

    const clearSources = () => {
      sources.text = ''
      sources.items = []
      sources.expanded = false
    }

    const onWebSearch = async () => {
      if (webSearchDisabled.value) {
        if (state.selfQA) {
          ElMessage.warning('「自问自答」模式没有具体问题可搜索')
        } else if (!(state.question || '').trim()) {
          ElMessage.warning('请先粘贴一个 Quora 原始问题')
        }
        return
      }
      sources.searching = true
      try {
        const res = await tabstoryApi.websearch({
          query: (state.question || '').trim(),
          max_items: 8,
        })
        sources.text = (res && res.sources_text) || ''
        sources.items = (res && res.items) || []
        sources.expanded = sources.items.length > 0
        if (!sources.items.length) {
          ElMessage.warning('未解析到结构化素材，原始返回已忽略，可重试或换问题表述')
        } else {
          ElMessage.success(`已检索到 ${sources.items.length} 条素材，将作为生成参考`)
        }
      } catch (e) {
        const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message || ''
        ElMessage.error('联网搜索失败：' + detail)
      } finally {
        sources.searching = false
      }
    }

    const toggleKeyword = (label) => {
      const i = state.keywords.indexOf(label)
      if (i >= 0) state.keywords.splice(i, 1)
      else state.keywords.push(label)
    }

    const resetOutput = () => {
      streamText.value = ''
      fullText.value = ''
      errorMsg.value = ''
      structured.target = ''
      structured.chineseRef = ''
      structured.keywordsUsed = []
      saved.value = false
      copied.value = false
    }

    const scrollOutputToBottom = () => {
      const el = outputBodyRef.value
      if (!el) return
      el.scrollTop = el.scrollHeight
    }

    /* ── 加载选项 ── */

    const loadOptions = async () => {
      loadingOptions.value = true
      try {
        const res = await tabstoryApi.getOptions()
        options.audiences = res.audiences || []
        options.angles    = res.angles    || []
        options.keywords  = res.keywords  || []
        options.tones     = res.tones     || []
        options.languages = res.languages || []
        // 默认语言：尝试英文，找不到就第一个
        if (!options.languages.find(l => l.value === state.language)) {
          state.language = (options.languages[0] || { value: 'en' }).value
        }
      } catch (e) {
        ElMessage.error('加载 Tabstory 选项失败：' + (e.message || ''))
      } finally {
        loadingOptions.value = false
      }
    }

    /* ── 生成（SSE 流式） ── */

    const finalizeFromFullText = () => {
      const parsed = parseOutput(fullText.value, state.language)
      structured.target = parsed.target
      structured.chineseRef = parsed.chineseRef
      structured.keywordsUsed = extractKeywordsUsed(parsed.kwText)
      phase.value = 'done'
    }

    const onGenerate = async () => {
      if (!canGenerate.value) {
        if (!state.audience || !state.angle || !state.tone) {
          ElMessage.warning('请先选择：目标受众、核心角度 和 语气风格')
        } else if (!state.selfQA && !(state.question || '').trim()) {
          ElMessage.warning('请粘贴一个 Quora 原始问题，或切换到「自问自答」')
        }
        return
      }

      resetOutput()
      phase.value = 'streaming'

      const body = {
        post_type: state.selfQA ? 'B' : 'A',
        audience: state.audience,
        angle: state.angle,
        keywords: state.keywords.slice(),
        tone: state.tone,
        language: state.language,
        question: state.selfQA ? '' : (state.question || '').trim(),
        sources_text: state.selfQA ? '' : (sources.text || ''),
      }

      abortCtrl = new AbortController()
      let res
      try {
        res = await fetch(`${resolveApiBaseURL()}/api/tabstory/generate-stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: abortCtrl.signal,
        })
      } catch (e) {
        errorMsg.value = '网络异常：' + (e.message || '')
        phase.value = 'idle'
        return
      }

      if (!res.ok) {
        errorMsg.value = `服务器错误 ${res.status}`
        phase.value = 'idle'
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let done = false

      try {
        while (!done) {
          const { done: d, value } = await reader.read()
          if (d) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()

          for (const rawLine of lines) {
            const line = rawLine.trim()
            if (!line.startsWith('data:')) continue
            const data = line.slice(5).trim()
            if (data === '[DONE]') {
              done = true
              break
            }
            try {
              const obj = JSON.parse(data)
              if (obj.error) {
                errorMsg.value = obj.error
                done = true
                break
              }
              if (obj.text) {
                fullText.value += obj.text
                streamText.value = fullText.value
                nextTick(scrollOutputToBottom)
              }
            } catch (e) {
              // 非法 JSON 段直接忽略
            }
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          errorMsg.value = '读取流失败：' + (e.message || '')
        }
      } finally {
        abortCtrl = null
      }

      if (errorMsg.value) {
        phase.value = 'idle'
        return
      }
      if (!fullText.value.trim()) {
        errorMsg.value = '生成结果为空，请重试'
        phase.value = 'idle'
        return
      }
      finalizeFromFullText()
    }

    /* ── 一键归档 ── */

    const onSave = async () => {
      if (!fullText.value.trim()) return
      saving.value = true
      try {
        await tabstoryApi.save({
          post_type: state.selfQA ? 'B' : 'A',
          audience: state.audience,
          angle: state.angle,
          keywords: state.keywords.slice(),
          tone: state.tone,
          language: state.language,
          question: state.selfQA ? '' : (state.question || '').trim(),
          sources_text: state.selfQA ? '' : (sources.text || ''),
          full_text: fullText.value,
        })
        saved.value = true
        ElMessage.success('已写入「文章管理」并在「任务队列」留档')
      } catch (e) {
        ElMessage.error('归档失败：' + (e.message || ''))
      } finally {
        saving.value = false
      }
    }

    /* ── 复制正文 ── */

    const onCopy = async () => {
      if (!structured.target) return
      try {
        await navigator.clipboard.writeText(structured.target)
        copied.value = true
        setTimeout(() => { copied.value = false }, 2000)
      } catch (e) {
        ElMessage.error('复制失败：' + (e.message || ''))
      }
    }

    onMounted(loadOptions)
    onActivated(() => {
      // 从「提示词模板」编辑回来，自动刷新选项
      loadOptions()
    })

    return {
      options, loadingOptions,
      state, phase, streamText, fullText, errorMsg, structured,
      saved, saving, copied, streaming,
      outputBodyRef,
      questionPlaceholder, canGenerate, canCopy, canSave,
      statusDotClass, targetLangLabel,
      toggleSelfQA, toggleKeyword,
      onGenerate, onSave, onCopy,
      sources, webSearchDisabled, onWebSearch, clearSources,
    }
  },
}
</script>

<style scoped>
/* ─── 颜色变量（仅当前页面） ─── */
.tabstory-page {
  /* 与全站设计令牌对齐（原紫色主题已统一为系统主色） */
  --primary: var(--mp-accent);
  --primary-dark: var(--mp-accent-hover);
  --primary-light: var(--mp-accent-soft);
  --primary-xlight: #f5f8ff;
  --accent: #6b95f2;
  --bg: transparent;
  --card: var(--mp-surface);
  --text: var(--mp-text);
  --text-muted: var(--mp-text-secondary);
  --text-light: var(--mp-text-muted);
  --border: var(--mp-border);
  --success: var(--mp-success);
  --error: #dc2626;
  --green-bg: var(--mp-success-soft);
  --green-border: #bfe8d9;
  --green-text: #0d6e50;
  --shadow-sm: var(--mp-shadow-sm);
  --radius: var(--mp-radius-lg);
  --radius-sm: 8px;

  color: var(--text);
  line-height: 1.6;
}


/* ── Layout ── */
.layout {
  display: grid;
  grid-template-columns: 390px 1fr;
  gap: 20px;
  margin: 0;
  padding: 0 0 40px;
  align-items: start;
}
@media (max-width: 1080px) {
  .layout { grid-template-columns: 1fr; }
}

/* ── Parameters panel ── */
.params-panel { display: flex; flex-direction: column; gap: 14px; }
.param-section {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px;
  box-shadow: var(--shadow-sm);
}
.param-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.7px;
  text-transform: uppercase; color: var(--primary);
  margin-bottom: 12px;
  display: flex; align-items: center; gap: 6px;
}
.param-label .step {
  width: 18px; height: 18px;
  background: var(--primary-light);
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; color: var(--primary);
  flex-shrink: 0;
}
.param-label .note {
  font-size: 10px; font-weight: 400; color: var(--text-muted);
  text-transform: none; letter-spacing: 0; margin-left: 2px;
}

/* Pill group */
.pill-group { display: flex; flex-wrap: wrap; gap: 7px; }
.pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 13px;
  border: 1.5px solid var(--border);
  border-radius: 99px;
  font-size: 12px; font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  background: var(--card);
  user-select: none;
  color: var(--text-muted);
}
.pill:hover { border-color: var(--accent); color: var(--primary); background: var(--primary-xlight); }
.pill.selected {
  border-color: var(--primary);
  background: var(--primary-light);
  color: var(--primary);
  font-weight: 600;
}
.pill .check-icon { display: none; font-size: 10px; }
.pill.selected .check-icon { display: inline; }

/* Angle grid */
.angle-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
.angle-card {
  border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 9px 11px;
  cursor: pointer;
  font-size: 12px; color: var(--text-muted);
  line-height: 1.4;
  transition: all 0.15s;
  background: var(--card);
  user-select: none;
}
.angle-card:hover { border-color: var(--accent); color: var(--primary); background: var(--primary-xlight); }
.angle-card.selected {
  border-color: var(--primary);
  background: var(--primary-light);
  color: var(--primary);
  font-weight: 500;
}

/* Language pills */
.lang-group { display: flex; flex-wrap: wrap; gap: 7px; }
.lang-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 14px;
  border: 1.5px solid var(--border);
  border-radius: 99px;
  font-size: 13px; font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  background: var(--card);
  user-select: none;
  color: var(--text-muted);
}
.lang-pill:hover { border-color: var(--accent); color: var(--primary); background: var(--primary-xlight); }
.lang-pill.selected {
  border-color: var(--primary);
  background: var(--primary-light);
  color: var(--primary);
  font-weight: 600;
}

/* Generate button */
.generate-btn {
  width: 100%; padding: 15px;
  background: linear-gradient(135deg, var(--primary-dark), var(--primary));
  color: #fff; border: none;
  border-radius: var(--radius);
  font-size: 15px; font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(50,108,244,0.28);
  letter-spacing: 0.2px;
}
.generate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(50,108,244,0.36);
}
.generate-btn:active:not(:disabled) { transform: translateY(0); }
.generate-btn:disabled {
  background: #C4B5FD;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
.btn-spinner {
  display: inline-block;
  width: 15px; height: 15px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 7px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Output panel ── */
.output-panel { position: sticky; top: 20px; }
.output-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}
.output-header {
  padding: 14px 18px;
  border-bottom: 1px solid var(--border);
  display: flex; align-items: center; justify-content: space-between;
  background: var(--primary-xlight);
  gap: 8px;
}
.output-title {
  font-size: 12px; font-weight: 700;
  color: var(--primary); letter-spacing: 0.4px;
  text-transform: uppercase;
}
.output-actions { display: flex; gap: 8px; align-items: center; }

.copy-btn {
  padding: 5px 14px;
  background: var(--primary);
  color: #fff;
  border: none; border-radius: 6px;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex; align-items: center; gap: 4px;
}
.copy-btn:hover:not(:disabled) { background: var(--primary-dark); }
.copy-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.copy-btn.copied { background: var(--success); }
.save-btn { background: #fff; color: var(--primary); border: 1.5px solid var(--primary); }
.save-btn:hover:not(:disabled) { background: var(--primary); color: #fff; }
.save-btn.copied { background: var(--success); color: #fff; border-color: var(--success); }

.status-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #D1D5DB;
  flex-shrink: 0;
  transition: background 0.3s;
}
.status-dot.streaming { background: #10B981; animation: pulse-dot 1s ease-in-out infinite; }
.status-dot.done      { background: var(--success); animation: none; }
.status-dot.error     { background: var(--error);   animation: none; }
@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.75); }
}

.output-body {
  min-height: 300px;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
}

.output-placeholder {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  min-height: 280px; gap: 10px;
  color: var(--text-light);
  text-align: center;
  padding: 40px 24px;
}
.output-placeholder .placeholder-icon { font-size: 36px; opacity: 0.35; }
.output-placeholder p { font-size: 13px; line-height: 1.6; margin: 0; }

.stream-raw {
  display: block;
  padding: 24px;
  font-size: 14px; line-height: 1.8;
  color: var(--text);
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  background: transparent;
  border: none;
  margin: 0;
}
.cursor {
  display: inline-block;
  width: 2px; height: 1em;
  background: var(--primary);
  margin-left: 1px;
  vertical-align: text-bottom;
  animation: blink 0.9s step-end infinite;
}
@keyframes blink { 50% { opacity: 0; } }

.bilingual-section { border-bottom: 1px solid var(--border); }
.bilingual-section:last-child { border-bottom: none; }
.bi-header {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.6px;
  padding: 9px 18px;
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid var(--border);
}
.bilingual-section.target-section .bi-header {
  background: var(--primary-xlight);
  color: var(--primary);
}
.bilingual-section.chinese-section .bi-header {
  background: var(--green-bg);
  color: var(--green-text);
  border-top: 1px solid var(--green-border);
}
.bi-body {
  padding: 20px 22px;
  font-size: 15px; line-height: 1.85;
  color: var(--text);
  white-space: pre-wrap;
  word-break: break-word;
}
.bilingual-section.chinese-section .bi-body {
  background: #FAFFFE;
  font-size: 14px;
  color: #1a2e25;
}

.keywords-section {
  border-top: 1px solid var(--border);
  padding: 12px 18px;
  display: none;
}
.keywords-section.show { display: block; }
.keywords-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.6px;
  text-transform: uppercase; color: var(--text-muted);
  margin-bottom: 7px;
}
.keyword-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.keyword-chip {
  padding: 3px 10px;
  background: var(--primary-light);
  color: var(--primary);
  border-radius: 99px;
  font-size: 11px; font-weight: 500;
}

.error-msg {
  color: var(--error); font-size: 13px;
  padding: 12px 16px; margin: 16px;
  background: #FEF2F2;
  border-radius: var(--radius-sm);
  border: 1px solid #FECACA;
  display: none;
}
.error-msg.show { display: block; }

/* Question box */
.question-box {
  background: linear-gradient(135deg, #FAF5FF, #F3E8FF);
  border: 1.5px solid var(--accent);
  border-radius: var(--radius);
  padding: 18px;
  box-shadow: var(--shadow-sm);
}
.question-box .param-label { color: var(--primary-dark); margin-bottom: 10px; }
.self-qa-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 9px;
  padding: 5px 13px;
  border: 1.5px solid var(--border);
  border-radius: 99px;
  font-size: 12px; font-weight: 500;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  background: #fff;
}
.self-qa-chip:hover { border-color: var(--accent); color: var(--primary); }
.self-qa-chip.active {
  border-color: var(--primary);
  background: var(--primary-light);
  color: var(--primary);
  font-weight: 600;
}
.question-textarea {
  width: 100%;
  min-height: 90px;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 10px 13px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.65;
  color: var(--text);
  resize: vertical;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  background: #fff;
}
.question-textarea:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--mp-accent-glow);
}
.question-textarea:disabled {
  background: #F5F3FF;
  color: var(--text-muted);
  cursor: not-allowed;
}
.question-textarea::placeholder { color: var(--text-light); }
.question-hint {
  font-size: 11px; color: var(--text-muted);
  margin-top: 7px; line-height: 1.5;
}

/* ─── 联网搜索（chip 行 + 素材面板） ─── */
.chip-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 9px;
}
.chip-row .self-qa-chip { margin-top: 0; }
.ws-chip {
  cursor: pointer;
}
.ws-chip.loading { color: var(--primary); border-color: var(--primary); }
.ws-chip.disabled {
  cursor: not-allowed;
  opacity: 0.55;
  color: var(--text-light);
}
.ws-chip.ws-action {
  color: var(--primary-dark);
  border-color: var(--primary);
  background: #FFFFFF;
}
.ws-chip.ws-clear {
  color: var(--error);
  border-color: var(--error);
  background: #FFFFFF;
}
.mini-spinner {
  width: 11px; height: 11px;
  border: 2px solid var(--primary-light);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: ts-mini-spin 0.8s linear infinite;
}
@keyframes ts-mini-spin {
  to { transform: rotate(360deg); }
}

.sources-panel {
  margin-top: 12px;
  padding: 12px 14px;
  background: #FFFFFF;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 280px;
  overflow-y: auto;
}
.source-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 8px;
  border-bottom: 1px dashed var(--border);
}
.source-item:last-of-type { border-bottom: none; padding-bottom: 0; }
.source-title {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-dark);
}
.source-no {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 18px;
  height: 18px;
  border-radius: 4px;
  background: var(--primary-light);
  color: var(--primary-dark);
  font-size: 11px;
  font-weight: 700;
  padding: 0 5px;
}
.source-name {
  flex: 1 1 auto;
  word-break: break-word;
}
.source-link {
  color: var(--primary);
  font-size: 13px;
  text-decoration: none;
}
.source-link:hover { text-decoration: underline; }
.source-snippet {
  font-size: 12px;
  line-height: 1.55;
  color: var(--text-muted);
}
.sources-tip {
  font-size: 11px;
  color: var(--text-light);
  margin-top: -2px;
}
</style>
